<script setup>
import { ref } from 'vue';
// import ActionMessage from '@/components/general/ActionMessage.vue';
// import FormSection from '@/components/general/FormSection.vue';
import InputError from '@/components/general/InputError.vue';
import InputLabel from '@/components/general/InputLabel.vue';
import PrimaryButton from '@/components/general/PrimaryButton.vue';
// import SecondaryButton from '@/components/general/SecondaryButton.vue';
import TextInput from '@/components/general/TextInput.vue';
import { useField } from 'vee-validate';
import { useFormValidation } from '@/composables/useFormValidation';
import { profileSchema } from '@/formValidationschemas';
import { useToast } from 'primevue/usetoast';
import api from '@/services/axios';
import { compressImage, formatFileSize } from '@/utils/imageCompression';
import mixpanel from '@/services/mixpanel';
const props = defineProps({
    user: Object,
});

const toast = useToast();
const verificationLinkSent = ref(null);
const photoPreview = ref(null);
const photoInput = ref(null);
const isSubmitting = ref(false);
const isCompressing = ref(false);
const recentlySuccessful = ref(false);

// Initialize form validation
const { handleSubmit, errors, resetForm } = useFormValidation(profileSchema, {
    name: props.user.name,
    email: props.user.email,
    photo: null
});

// Connect form fields
const { value: name } = useField('name');
const { value: email } = useField('email');
const { value: photo, setValue: setPhoto } = useField('photo');

const updateProfileInformation = handleSubmit(async (values) => {
    try {
        isSubmitting.value = true;

        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('email', values.email);
        formData.append('_method', 'PUT');

        // Only include photo if a new one is selected
        if (photoInput.value && photoInput.value.files[0]) {
            // Use the compressed photo from the form field instead of the original file
            const photoFile = photo.value;
            if (photoFile) {
                formData.append('photo', photoFile);
            } else {
                // Fallback to original file if compression failed
                formData.append('photo', photoInput.value.files[0]);
            }
        }

        const response = await api.post('api/user/profile-information', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        // Update the user object with the response data
        if (response.data && response.data.user) {
            // Update the user object with the new data
            Object.assign(props.user, response.data.user);

            // Keep the preview if we just uploaded a new photo
            if (!photoInput.value || !photoInput.value.files[0]) {
                photoPreview.value = null;
            }
        }

        clearPhotoFileInput();

        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Profile updated successfully!',
            life: 3000
        });

        recentlySuccessful.value = true;
        setTimeout(() => {
            recentlySuccessful.value = false;
        }, 2000);

    } catch (error) {
        const errorMessage = error.response.data.message;

        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 3000
        });
    } finally {
        isSubmitting.value = false;
    }
});

const sendEmailVerification = async () => {
    try {
        await api.post('email/verification-notification');
        verificationLinkSent.value = true;

        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Verification link sent!',
            life: 3000
        });
    } catch (error) {
        const errorMessage = error.response.data.message;
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 3000
        });
    }
};

const selectNewPhoto = () => {
    photoInput.value.click();
};

const updatePhotoPreview = async () => {
    const photo = photoInput.value.files[0];

    if (!photo) return;

    isCompressing.value = true;

    try {
        // Compress the image before setting it
        const compressedPhoto = await compressImage(photo, {
            maxWidth: 800,
            maxHeight: 800,
            quality: 0.8,
            maxSizeMB: 1
        });

        setPhoto(compressedPhoto);

        const reader = new FileReader();

        reader.onload = (e) => {
            photoPreview.value = e.target.result;
        };

                reader.readAsDataURL(compressedPhoto);

        // Show compression info in toast
        // const originalSize = formatFileSize(photo.size);
        // const compressedSize = formatFileSize(compressedPhoto.size);
        const compressionRatio = Math.round(((photo.size - compressedPhoto.size) / photo.size) * 100);

        if (compressionRatio > 0) {
            // toast.add({
            //     severity: 'info',
            //     summary: 'Image Compressed',
            //     detail: `Original: ${originalSize} → Compressed: ${compressedSize} (${compressionRatio}% smaller)`,
            //     life: 3000
            // });
        }
    } catch (error) {
        // Fallback to original image if compression fails
        setPhoto(photo);

        const reader = new FileReader();
        reader.onload = (e) => {
            photoPreview.value = e.target.result;
        };
        reader.readAsDataURL(photo);
    } finally {
        isCompressing.value = false;
    }
};

const deletePhoto = async () => {
    try {
        const response = await api.delete('api/user/profile-photo');

        // Update the user object with the response data
        if (response.data && response.data.user) {
            // Update the user object with the new data
            Object.assign(props.user, response.data.user);
        }

        photoPreview.value = null;
        clearPhotoFileInput();

        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Profile photo removed successfully!',
            life: 3000
        });
    } catch (error) {

        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to remove profile photo. Please try again.',
            life: 3000
        });
    }
};

const clearPhotoFileInput = () => {
    if (photoInput.value && photoInput.value.value) {
        photoInput.value.value = null;
    }
};
</script>

<template>
    <section>
        <div>
            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">Profile Information</h2>

            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Update your account's profile information and email address.
            </p>
        </div>

        <form @submit.prevent="updateProfileInformation" class="mt-6 space-y-6">
            <div>
                <InputLabel for="name" value="Name" />

                <TextInput
                    id="name"
                    type="text"
                    class="block mt-1 w-full"
                    v-model="name"
                    autofocus
                    autocomplete="name"
                />

                <InputError class="mt-2" :message="errors.name" />
            </div>

            <!-- <div>
                <InputLabel for="email" value="Email" />

                <TextInput
                    id="email"
                    type="email"
                    class="block mt-1 w-full"
                    v-model="email"
                    autocomplete="username"
                />

                <InputError class="mt-2" :message="errors.email" />
            </div> -->

            <div>
                <InputLabel for="photo" value="Profile Photo" />
                <div class="flex items-center mt-2">
                    <img v-if="photoPreview" :src="photoPreview" class="object-cover flex-shrink-0 w-20 h-20 rounded-full" />
                    <img v-else-if="user.image_url" :src="user.image_url" class="object-cover flex-shrink-0 w-20 h-20 rounded-full" />
                    <span v-else class="inline-block overflow-hidden flex-shrink-0 w-20 h-20 bg-gray-100 rounded-full">
                        <svg class="w-full h-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                    </span>
                    <div class="items-center space-y-2 sm:flex sm:space-y-0">
                      <button
                          type="button"
                          class="px-3 py-2 ml-5 text-sm font-medium leading-4 text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          @click="selectNewPhoto"
                          :disabled="isCompressing"
                      >
                          <span v-if="isCompressing" class="flex items-center">
                              <svg class="mr-2 w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Compressing...
                          </span>
                          <span v-else>
                              Select A New Photo
                          </span>
                      </button>

                      <button
                          v-if="user.image_url"
                          type="button"
                          class="px-3 py-2 ml-5 text-sm font-medium leading-4 text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          @click="deletePhoto"
                      >
                          Remove Photo
                      </button>
                    </div>
                </div>

                <input
                    ref="photoInput"
                    type="file"
                    class="hidden"
                    @change="updatePhotoPreview"
                >

                <InputError class="mt-2" :message="errors.photo" />
            </div>

            <div v-if="user.email_verified_at === null">
                <p class="mt-2 text-sm text-gray-800 dark:text-gray-400">
                    Your email address is unverified.
                    <button
                        @click.prevent="sendEmailVerification"
                        type="button"
                        class="text-sm text-gray-600 underline rounded-md hover:dark:text-gray-300 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:text-gray-400"
                    >
                        Click here to re-send the verification email.
                    </button>
                </p>

                <div
                    v-show="verificationLinkSent"
                    class="mt-2 text-sm font-medium text-green-600"
                >
                    A new verification link has been sent to your email address.
                </div>
            </div>

            <div class="flex gap-4 items-center">
                <PrimaryButton :disabled="isSubmitting" @click="mixpanel.track('Save Profile Information')">{{ isSubmitting ? 'Saving...' : 'Save' }}</PrimaryButton>

                <Transition
                    enter-active-class="transition ease-in-out"
                    enter-from-class="opacity-0"
                    leave-active-class="transition ease-in-out"
                    leave-to-class="opacity-0"
                >
                    <p v-if="recentlySuccessful" class="text-sm text-gray-600">Saved.</p>
                </Transition>
            </div>
        </form>
    </section>
</template>
